/**
 * Scope Breakdown Chart
 *
 * Donut chart showing distribution of emissions by scope.
 * Legend includes progress bars showing relative proportions.
 */

import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, PieChart as PieChartIcon, Loader2 } from 'lucide-react';
import { PieChart, Pie, Cell, Label } from 'recharts';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl } from '@routes/util';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import type { EmissionSummary } from '../types/emissions';
import { SCOPE_COLORS } from '../config/scopeBranding';

interface ScopeBreakdownChartProps {
  initiativeId: string;
  summary?: EmissionSummary;
  isLoading?: boolean;
}

const scopeConfig = {
  scope1: { label: 'Scope 1', description: 'Direct emissions' },
  scope2: { label: 'Scope 2', description: 'Purchased energy' },
  scope3: { label: 'Scope 3', description: 'Value chain' },
};

const chartConfig = {
  emissions: { label: 'Emissions' },
  scope1: { label: 'Scope 1', color: SCOPE_COLORS.scope1 },
  scope2: { label: 'Scope 2', color: SCOPE_COLORS.scope2 },
  scope3: { label: 'Scope 3', color: SCOPE_COLORS.scope3 },
} satisfies ChartConfig;

export const ScopeBreakdownChart = ({ initiativeId, summary, isLoading }: ScopeBreakdownChartProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const history = useHistory();
  const viewAllUrl = generateUrl(
    emissionsTrackerRoutes.EMISSIONS_OVERVIEW,
    { rootAppPath, initiativeId },
  );
  const scope1 = summary?.byScope?.scope1 || 0;
  const scope2 = summary?.byScope?.scope2 || 0;
  const scope3 = summary?.byScope?.scope3 || 0;
  const total = scope1 + scope2 + scope3;

  const chartData = useMemo(() => [
    { scope: 'scope1', emissions: scope1, fill: SCOPE_COLORS.scope1 },
    { scope: 'scope2', emissions: scope2, fill: SCOPE_COLORS.scope2 },
    { scope: 'scope3', emissions: scope3, fill: SCOPE_COLORS.scope3 },
  ].filter((d) => d.emissions > 0), [scope1, scope2, scope3]);

  const scopeData = useMemo(() => [
    { key: 'scope1' as const, value: scope1 },
    { key: 'scope2' as const, value: scope2 },
    { key: 'scope3' as const, value: scope3 },
  ], [scope1, scope2, scope3]);

  const hasData = total > 0;

  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-start tw:gap-3'>
          <div>
            <CardTitle>Scope Breakdown</CardTitle>
            <CardDescription>Distribution of emissions by scope</CardDescription>
          </div>
          <Button
            variant='link'
            size='sm'
            onClick={() => history.push(viewAllUrl)}
          >
            View all
            <ArrowRight />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='tw:flex tw:items-center tw:justify-center tw:h-[280px]'>
            <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : !hasData ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:h-[280px] tw:text-muted-foreground'>
            <PieChartIcon className='tw:size-10 tw:mb-2 tw:opacity-50' />
            <p className='tw:text-sm'>No emissions data</p>
          </div>
        ) : (
          <div className='tw:flex tw:items-center tw:h-[300px]'>
            <ChartContainer config={chartConfig} className='tw:w-[50%] tw:h-full'>
              <PieChart>
                <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                <Pie
                  data={chartData}
                  dataKey='emissions'
                  nameKey='scope'
                  innerRadius={68}
                  outerRadius={100}
                  strokeWidth={3}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.scope} fill={entry.fill} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                        const totalFormatted = formatCompact(total);
                        return (
                          <text x={viewBox.cx} y={viewBox.cy} textAnchor='middle' dominantBaseline='middle'>
                            <tspan x={viewBox.cx} y={viewBox.cy} className='tw:fill-foreground tw:text-2xl tw:font-bold'>
                              {totalFormatted.formatted}
                            </tspan>
                            <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 20} className='tw:fill-muted-foreground tw:text-xs'>
                              {totalFormatted.unit}
                            </tspan>
                          </text>
                        );
                      }
                      return null;
                    }}
                  />
                </Pie>
              </PieChart>
            </ChartContainer>

            <div className='tw:flex-1 tw:pl-4 tw:space-y-5'>
              {scopeData.map(({ key, value }) => {
                const config = scopeConfig[key];
                const hasValue = value > 0;
                const percentage = hasValue ? Math.round((value / total) * 100) : 0;
                return (
                  <div key={key}>
                    <div className='tw:flex tw:items-center tw:justify-between tw:mb-1'>
                      <div className='tw:flex tw:items-center tw:gap-2'>
                        <span
                          className='tw:size-2.5 tw:rounded-full tw:shrink-0'
                          style={{ backgroundColor: SCOPE_COLORS[key] }}
                        />
                        <span className='tw:text-sm tw:font-medium'>{config.label}</span>
                      </div>
                      <span className='tw:text-sm tw:font-bold'>
                        {hasValue ? formatCompact(value).display : '—'}
                      </span>
                    </div>
                    <Progress
                      value={percentage}
                      className='tw:h-1.5 tw:mb-1'
                      progressColor={SCOPE_COLORS[key]}
                    />
                    <p className='tw:text-xs tw:text-muted-foreground'>
                      {config.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
